/**
 * LiveViewModal — superadmin viewer. Replays the target's app session in real
 * time (rrweb live mode) from event chunks streamed by the target's app.
 * Closing the window ends the session; sessions also expire after 45 idle min.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import api from "@/lib/api";
import "rrweb/dist/style.css";

export default function LiveViewModal({ sessionId, targetName, onClose }) {
  const stageRef = useRef(null);
  const [status, setStatus] = useState("waiting"); // waiting | live | ended

  // mute underlying dialog layers' pointer events while watching live, and
  // flag the modal so SettingsDialog doesn't close on "outside" taps
  useEffect(() => {
    window.__liveViewOpen = true;
    const els = document.querySelectorAll("[data-dialog-overlay], [data-dialog-content]");
    const saved = [...els].map((el) => [el, el.style.pointerEvents]);
    els.forEach((el) => { el.style.pointerEvents = "none"; });
    return () => {
      window.__liveViewOpen = false;
      saved.forEach(([el, v]) => { el.style.pointerEvents = v; });
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let last = 0;
    let timer = null;
    let replayer = null;

    const fit = () => {
      const stage = stageRef.current;
      const player = stage?.querySelector(".replayer-wrapper");
      const frame = stage?.querySelector("iframe");
      if (!stage || !player || !frame || !frame.clientWidth || !frame.clientHeight) return;
      const scale = Math.min(stage.clientWidth / frame.clientWidth, stage.clientHeight / frame.clientHeight, 1);
      player.style.transform = `scale(${scale})`;
      player.style.transformOrigin = "top left";
      player.style.position = "absolute";
    };

    (async () => {
      const { Replayer } = await import("rrweb");
      if (!alive || !stageRef.current) return;
      replayer = new Replayer([], { root: stageRef.current, liveMode: true, mouseTail: true });
      replayer.startLive();

      const pull = async () => {
        if (!alive) return;
        try {
          const { data } = await api.get(`/liveview/${sessionId}/events`, { params: { after: last } });
          const chunks = data.chunks || [];
          for (const ch of chunks) {
            for (const e of ch.events) {
              try { replayer.addEvent(e); } catch { /* drop malformed */ }
            }
            last = ch.seq;
          }
          if (chunks.length) {
            setStatus("live");
            setTimeout(fit, 50);
          }
        } catch (e) {
          if (e?.response?.status === 404) {
            setStatus("ended");
            return;
          }
        }
        timer = setTimeout(pull, 2000);
      };
      pull();
    })();

    return () => {
      alive = false;
      clearTimeout(timer);
      try { replayer?.destroy(); } catch { /* noop */ }
    };
  }, [sessionId]);

  const close = async () => {
    try { await api.post(`/liveview/${sessionId}/stop`); } catch { /* noop */ }
    onClose();
  };

  return createPortal(
    <div className="pointer-events-auto fixed inset-0 z-[200] flex items-center justify-center bg-black/85 p-3" data-testid="liveview-modal">
      <div className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-white/15 bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className={`absolute inline-flex h-full w-full rounded-full ${status === "live" ? "animate-ping bg-red-500 opacity-60" : "bg-gray-500"}`} />
              <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${status === "live" ? "bg-red-500" : "bg-gray-500"}`} />
            </span>
            <p className="text-sm font-semibold text-white" data-testid="liveview-title">
              Live — {targetName}
            </p>
          </div>
          <button onClick={close} data-testid="liveview-close" aria-label="Close live view"
            className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="relative flex-1 overflow-hidden bg-white">
          <div ref={stageRef} className="h-full w-full overflow-hidden" data-testid="liveview-stage" />
          {status !== "live" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0d0d0d] text-center">
              {status === "waiting" ? (
                <>
                  <Loader2 className="h-6 w-6 animate-spin text-[#8fa4f0]" />
                  <p className="max-w-sm px-4 text-xs text-white/55" data-testid="liveview-waiting">
                    Waiting for {targetName}&apos;s app to connect — they need the app open. This can take up to ~30 seconds.
                  </p>
                </>
              ) : (
                <p className="text-xs text-white/55" data-testid="liveview-ended">The live session has ended.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
